import { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, BookOpen } from 'lucide-react';

const MagazinesManager = () => {
    const [magazines, setMagazines] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMagazines = async () => {
        try {
            const q = query(collection(db, "magazines"));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by date (DD.MM.YYYY) descending
            data.sort((a, b) => {
                if (!a.date || !b.date) return 0;
                const [d1, m1, y1] = a.date.split('.');
                const [d2, m2, y2] = b.date.split('.');
                return new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1);
            });

            setMagazines(data);
        } catch (error) {
            console.error("Error fetching magazines:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMagazines();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Bu dergiyi silmek istediğinize emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "magazines", id));
                setMagazines(magazines.filter(m => m.id !== id));
            } catch (error) {
                console.error("Error deleting magazine:", error);
                alert("Silme işlemi başarısız oldu.");
            }
        }
    };

    if (loading) return <div className="text-white">Yükleniyor...</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-white">Dergimiz</h1>
                <Link
                    to="/admin/magazines/new"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <Plus size={20} />
                    <span>Yeni Sayı Ekle</span>
                </Link>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <table className="w-full text-left text-gray-400 hidden md:table">
                    <thead className="bg-gray-700/50 text-gray-200 uppercase text-xs">
                        <tr>
                            <th className="px-6 py-4">Kapak</th>
                            <th className="px-6 py-4">Başlık / Sayı</th>
                            <th className="px-6 py-4">Yayın Tarihi</th>
                            <th className="px-6 py-4 text-right">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {magazines.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                    Henüz hiç dergi eklenmemiş.
                                </td>
                            </tr>
                        ) : (
                            magazines.map((mag) => (
                                <tr key={mag.id} className="hover:bg-gray-700/30 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="w-12 h-16 bg-gray-700 rounded overflow-hidden">
                                            {mag.coverImage ? (
                                                <img src={mag.coverImage} alt={mag.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <BookOpen size={16} />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-white">
                                        {mag.title}
                                        <div className="text-xs text-gray-500 mt-1">{mag.pdfUrl ? 'PDF Linki Var' : 'PDF Yok'}</div>
                                    </td>
                                    <td className="px-6 py-4">{mag.date}</td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-3">
                                        <Link
                                            to={`/admin/magazines/edit/${mag.id}`}
                                            className="text-blue-400 hover:text-blue-300 p-1 rounded-md hover:bg-blue-400/10 transition-colors"
                                        >
                                            <Edit2 size={18} />
                                        </Link>
                                        <button
                                            onClick={() => handleDelete(mag.id)}
                                            className="text-red-400 hover:text-red-300 p-1 rounded-md hover:bg-red-400/10 transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="md:hidden flex flex-col divide-y divide-gray-700">
                    {magazines.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            Henüz hiç dergi eklenmemiş.
                        </div>
                    ) : (
                        magazines.map((mag) => (
                            <div key={mag.id} className="p-4 flex gap-4 items-center">
                                {/* Cover (Left) */}
                                <div className="w-16 h-24 bg-gray-700 rounded overflow-hidden flex-shrink-0">
                                    {mag.coverImage ? (
                                        <img src={mag.coverImage} alt={mag.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <BookOpen size={20} />
                                        </div>
                                    )}
                                </div>

                                {/* Content (Middle) */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-white font-bold truncate text-lg mb-1">{mag.title}</h3>
                                    <div className="text-gray-400 text-sm flex flex-col gap-1">
                                        <span>{mag.date}</span>
                                        <span className={`text-xs ${mag.pdfUrl ? 'text-green-400' : 'text-red-400'}`}>
                                            {mag.pdfUrl ? 'PDF Yüklü' : 'PDF Yok'}
                                        </span>
                                    </div>
                                </div>

                                {/* Actions (Right) */}
                                <div className="flex flex-col gap-2">
                                    <Link
                                        to={`/admin/magazines/edit/${mag.id}`}
                                        className="p-3 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500 hover:text-white transition-colors"
                                    >
                                        <Edit2 size={20} />
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(mag.id)}
                                        className="p-3 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default MagazinesManager;
